import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { resetPassword } from "../../api/authApi";

import InputLabel from "../Shared/InputLabel";
import Input from "../Shared/Input";
import Button from "./Button";
import FormWrapper from "./FormWrapper";
import AlertGreen from "./AlertGreen";
import AlertRed from "../Shared/AlertRed";
import PasswordStrengthIndicator from "../Shared/PasswordStrengthIndicator";
import { getPasswordValidationRules } from "../../utils/passwordValidation";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("token") || null;
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
  });
  const newPasswordValue = watch("newPassword");

  useEffect(() => {
    if (redirectCountdown === null) return;
    if (redirectCountdown === 0) {
      navigate("/signin");
      return;
    }
    const timer = setTimeout(() => setRedirectCountdown(redirectCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [redirectCountdown, navigate]);

  const onSubmit = async (data) => {
    try {
      await resetPassword(token, data);
      setRedirectCountdown(5);
    } catch {
      setAlert(
        <AlertRed
          title="Oops! Something Went Wrong"
          message="We encountered an issue while updating your password. Please try again later or contact support if the problem persists."
        />
      );
    }
  };

  if (!token) {
    return (
      <div>
        <FormWrapper>
          <AlertRed
            title="Invalid or Missing Reset Link"
            message="This password reset link is invalid or has expired. Please request a new one."
          />
          <div className="mt-4">
            <Link
              to="/forgot-password"
              className="flex w-full justify-center rounded-md bg-accent-500 px-3 py-2 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-accent-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
            >
              Request a new reset link
            </Link>
          </div>
        </FormWrapper>
      </div>
    );
  }

  return (
    <div>
      <FormWrapper>
        <p className="text-base text-ink-700 mb-4">
          Create a new password for your account.
        </p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <InputLabel htmlFor="newPassword">Password</InputLabel>
            <Input
              id="newPassword"
              type="password"
              errormessage={errors?.newPassword?.message}
              {...register("newPassword", getPasswordValidationRules())}
            />
            <PasswordStrengthIndicator password={newPasswordValue} />
          </div>
          <div>
            <InputLabel htmlFor="confirmPassword">Confirm Password</InputLabel>
            <Input
              id="confirmPassword"
              type="password"
              errormessage={errors?.confirmPassword?.message}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === newPasswordValue || "Passwords do not match",
              })}
            />
          </div>
          {redirectCountdown !== null ? (
            <AlertGreen
              title="Password Updated Successfully"
              message={
                <>
                  Your password has been changed. Redirecting to sign in
                  {redirectCountdown > 0
                    ? ` in ${redirectCountdown} second${redirectCountdown !== 1 ? "s" : ""}...`
                    : "..."}{" "}
                  <Link
                    to="/signin"
                    className="font-semibold leading-6 text-accent-600 hover:text-accent-500"
                  >
                    Sign in now
                  </Link>
                </>
              }
            />
          ) : (
            alert
          )}
          {redirectCountdown === null ? (
            <div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent-500 hover:bg-accent-400 focus-visible:outline-accent-500"
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          ) : null}
        </form>
      </FormWrapper>
    </div>
  );
};

export default ResetPassword;
